import "./App.css";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { BasePageLayout } from "./components/Layouts/BasePage";
import { Router } from "./router/router";
import { useDispatch } from "react-redux";
import { PageConfigActions } from "./store/PageConfigurations";
import React, { useEffect, useState } from "react";
import BookingApi from "./API/Apis/BookingApi";
import { BOOKING_ACTIONS } from "./store/onlineBooking";
import i18n from "./config/i18n";
import theme, {
  formatTheme,
  setIsGretaNewUi,
  onThemeUpdated,
} from "./config/MuiColorThemes";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { GoogleOAuthProvider } from "@react-oauth/google";
import BookingGetters from "./store/onlineBooking/getters";
import { initTagManager } from "./helpers/tracking";
/*function getLanguageFromUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("lang") || "it";
}*/

const STATIC_FONTS = ["roboto", "montserrat"];

function loadTenantFont(family: string): void {
  const name = family.trim();
  if (!name || STATIC_FONTS.includes(name.toLowerCase())) return;

  const linkId = "tenant-custom-font";
  let link = document.getElementById(linkId) as HTMLLinkElement | null;
  if (link && link.dataset.family === name) return; // already loaded this font
  if (!link) {
    link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.dataset.family = name;

  const encoded = encodeURIComponent(name).replace(/%20/g, "+");
  const withWeights = `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;500;600;700&display=swap`;
  const noWeights = `https://fonts.googleapis.com/css2?family=${encoded}&display=swap`;
  link.onerror = () => {
    if (link && link.href !== noWeights) link.href = noWeights;
  };
  link.href = withWeights;
}
function App() {
  useEffect(() => {
    if (process.env.REACT_APP_COMPANY_CODE === "viaggiare") {
      const affiliredScript = document.createElement("script");
      affiliredScript.type = "text/javascript";
      affiliredScript.async = true;
      affiliredScript.src =
        "https://customs.affilired.com/track/?merchant=4918";
      document.body.appendChild(affiliredScript);
    }
  }, []);

  const [, forceThemeUpdate] = useState(0);
  useEffect(() => {
    const unsubscribe = onThemeUpdated(() => forceThemeUpdate((v) => v + 1));
    return unsubscribe;
  }, []);

  const dynamicPalette = formatTheme();
  const dynamicTheme = createTheme(dynamicPalette);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(PageConfigActions.setHomePageConfig({}));
    dispatch(PageConfigActions.setHeaderConfig({}));
    dispatch(PageConfigActions.setFooterConfig({}));
    BookingApi.getAppSetting().then((e) => {
      dispatch(BOOKING_ACTIONS.setAppSetting(e));
      initTagManager(e);
      setIsGretaNewUi(!!e?.isGretaBookingNewUi);
      //for felirent font-family will be Montserrat and for every other client it is by default
      const fontFamilyStyle = e?.fontFamily;
      if (fontFamilyStyle) {
        // a single place — the `:root { --font-primary }` rule in index.css,
        // which reads this via `var(--font-custom, ...)`
        document.documentElement.style.setProperty(
          "--font-custom",
          fontFamilyStyle
        );
        // applies — nothing to load here.
        loadTenantFont(fontFamilyStyle);
      }
    });
    BookingApi.getPrivacyAndTerms().then((e) => {
      dispatch(BOOKING_ACTIONS.setRentalTerms(e.data.conditionText));
    });
    /*const language = getLanguageFromUrl();
    BookingApi.getTextResource(language).then((response) => {
      const textResource: BookingTextResource = response.data;
      dispatch(BOOKING_ACTIONS.setTextResource(textResource));
    });*/
  }, []);
  const [queryParams] = useSearchParams();
  useEffect(() => {
    const currentLanguage = queryParams.get("lang");
    if (currentLanguage && i18n.languages.includes(currentLanguage)) {
      sessionStorage.setItem("preferredLang", currentLanguage);
      i18n.changeLanguage(currentLanguage);
    } else {
      const storedLang = sessionStorage.getItem("preferredLang");
      if (storedLang && i18n.languages.includes(storedLang)) {
        i18n.changeLanguage(storedLang);
      }
    }
  }, []);

  const appSettings = BookingGetters.getAppSettings();
  const clientId = appSettings?.googleClientID || "";
  return (
    <GoogleOAuthProvider clientId={clientId}>
      <ThemeProvider theme={dynamicTheme || theme}>
        <BasePageLayout>
          <div>
            <Routes>
              {Router.map((el, index) => {
                let shouldRedirect = false;
                let redirectUrl = "/";
                if (el.shouldRedirect) {
                  shouldRedirect = el.shouldRedirect();
                }
                if (el.redirectUrl) {
                  redirectUrl = el.redirectUrl;
                }
                return (
                  <Route
                    key={index}
                    path={el.path}
                    element={
                      <ProtectedRoute
                        shouldRedirect={shouldRedirect}
                        url={redirectUrl}
                        child={el.element}
                      ></ProtectedRoute>
                    }
                  >
                    {el.children &&
                      el.children.map((nested, id) => {
                        return (
                          <Route
                            key={id}
                            path={nested.path}
                            element={
                              <ProtectedRoute
                                shouldRedirect={shouldRedirect}
                                url={redirectUrl}
                                child={nested.element}
                              />
                            }
                          />
                        );
                      })}
                  </Route>
                );
              })}
            </Routes>
          </div>
        </BasePageLayout>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}
interface ProtectedRouteProps {
  child: Element | JSX.Element;
  shouldRedirect: boolean;
  url: string;
  children?: any;
}

function ProtectedRoute({
  child,
  shouldRedirect,
  url = "/",
}: ProtectedRouteProps): JSX.Element {
  if (shouldRedirect) {
    return <Navigate to={url} />;
  }
  return <>{child}</>;
}
export default App;
